import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import ParticleCanvas from './ParticleCanvas';
import { MaskedLine, FadeIn } from './Reveal';
import { IMAGES } from '../lib/site';

const EASE = [0.22, 1, 0.36, 1];

export default function KineticHero() {
    const ref = useRef(null);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const onMouseMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        setTilt({
            x: (e.clientX - r.left) / r.width - 0.5,
            y: (e.clientY - r.top) / r.height - 0.5,
        });
    };

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="dark-section relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
        >
            <ParticleCanvas className="absolute inset-0 w-full h-full" />
            <div
                className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(138,147,245,0.14) 0%, transparent 65%)' }}
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
                <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
                    <FadeIn delay={0.1}>
                        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-bronze mb-8 flex items-center gap-3">
                            <span className="inline-block w-10 h-px bg-bronze" />
                            Integrated Strategic Program Management — Est. 2015
                        </p>
                    </FadeIn>

                    <h1
                        data-testid="hero-headline"
                        className="font-serif font-bold tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] text-cream"
                    >
                        <MaskedLine delay={0.2}>Architects of Influence.</MaskedLine>
                        <MaskedLine delay={0.35}>
                            <span className="text-bronze">Fractional PR</span>
                        </MaskedLine>
                        <MaskedLine delay={0.5}>and Strategy Agency.</MaskedLine>
                    </h1>

                    <FadeIn delay={0.85}>
                        <p
                            data-testid="hero-subheading"
                            className="mt-8 max-w-xl text-base sm:text-lg text-fog leading-relaxed"
                        >
                            CommPro Worldwide is a fractional PR and strategy agency designing
                            integrated programs that drive market adoption, public-private
                            partnerships, executive and media visibility, event activation, and lead
                            generation — with program management support built to scale.
                        </p>
                    </FadeIn>

                    <FadeIn delay={1}>
                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <Link
                                to="/contact"
                                data-testid="hero-consultation-btn"
                                className="group inline-flex items-center gap-3 bg-bronze text-ink font-mono text-[11px] uppercase tracking-[0.22em] px-8 py-4 rounded-full relative overflow-hidden"
                            >
                                <span className="absolute inset-0 bg-gold translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out rounded-full" />
                                <span className="relative">Request Consultation</span>
                                <ArrowRight size={14} className="relative group-hover:translate-x-1 transition-transform duration-300" />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-services-btn"
                                className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cream border border-line px-8 py-4 rounded-full hover:border-bronze transition-colors duration-500"
                            >
                                Explore Services
                                <ArrowUpRight size={14} className="text-bronze group-hover:rotate-45 transition-transform duration-300" />
                            </Link>
                        </div>
                    </FadeIn>

                    <FadeIn delay={1.15}>
                        <div className="mt-14 flex flex-wrap gap-x-12 gap-y-6">
                            {[
                                ['10+', 'Years of Counsel'],
                                ['140+', 'Global Activations'],
                                ['32', 'Markets Entered'],
                            ].map(([v, l]) => (
                                <div key={l}>
                                    <p className="font-serif text-3xl font-bold text-cream">{v}</p>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fog mt-1">{l}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>
                </motion.div>

                <div className="lg:col-span-5 relative" style={{ perspective: '1200px' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 60 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
                        style={{ y: imgY }}
                        className="relative"
                    >
                        <motion.div
                            animate={{ rotateY: tilt.x * 5, rotateX: -tilt.y * 5 }}
                            transition={{ type: 'spring', stiffness: 60, damping: 20 }}
                            className="relative"
                        >
                            <div className="absolute -inset-3 border border-bronze/30 translate-x-5 translate-y-5 pointer-events-none" />
                            <div className="relative overflow-hidden">
                                <img
                                    src={IMAGES.podium}
                                    alt="Executive speaking at a press conference"
                                    className="w-full aspect-[4/5] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/20" />
                                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                                    <div>
                                        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bronze">
                                            Executive Visibility
                                        </p>
                                        <p className="font-serif font-semibold text-lg text-cream mt-1">
                                            The podium is a strategy.
                                        </p>
                                    </div>
                                    <span className="font-mono text-[9px] text-fog">01 / 06</span>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
            >
                <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-fog">Scroll</span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                    className="block w-px h-10 bg-gradient-to-b from-bronze to-transparent"
                />
            </motion.div>
        </section>
    );
}
